import { getApi } from "../../../helpers/auth_helpers";
import { buyStudioModule } from "../../../helpers/saasCheckout";

const base = () => String(process.env.REACT_APP_SERVERURL || "").replace(/\/+$/, "");

/** AI credits: 1 credit = ₹1, kept in paise on the server. */
export type AiCreditPack = { id: string; credits?: number; pricePaise: number; label?: string };
export type AiCreditRow = {
  type: "STARTER" | "PURCHASE" | "ADMIN_GRANT" | "USAGE" | string;
  amountPaise: number;
  feature?: string;
  note?: string;
  createdAt?: string;
};
export type AiCredits = {
  balancePaise: number;
  overdraftPaise: number;
  packs: AiCreditPack[];
  estimatePaise: Record<string, number>;
  recent: AiCreditRow[];
};

/** "₹19.82" — credits are rupees, shown to 2 decimals. */
export const formatCredits = (paise: number | null | undefined): string => {
  const n = Number(paise || 0) / 100;
  const sign = n < 0 ? "−" : "";
  return `${sign}₹${Math.abs(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/** Writing is allowed until the balance reaches the overdraft floor (e.g. −₹10). */
export const canSpend = (credits: Pick<AiCredits, "balancePaise" | "overdraftPaise"> | null): boolean =>
  !credits || credits.balancePaise > -Math.abs(credits.overdraftPaise || 0);

export const fetchAiCredits = async (): Promise<AiCredits | null> => {
  try {
    const res: any = await getApi(`${base()}/ai-credits`);
    const d = res?.data;
    if (!res?.success || !d) return null;
    return {
      balancePaise: Number(d.balancePaise || 0),
      overdraftPaise: Number(d.overdraftPaise || 0),
      packs: Array.isArray(d.packs) ? d.packs : [],
      estimatePaise: d.estimatePaise || {},
      recent: Array.isArray(d.recent) ? d.recent : [],
    };
  } catch {
    return null;
  }
};

/** Buys a credit pack through the existing Razorpay studio checkout. Resolves "" on success, else a message. */
export const buyCreditPack = (pack: AiCreditPack): Promise<string> =>
  buyStudioModule(pack.id, pack.label || `AI credits ${formatCredits(pack.pricePaise)}`);

export const isCreditsExhausted = (res: any): boolean =>
  res?.code === "AI_CREDITS_EXHAUSTED" || res?.data?.code === "AI_CREDITS_EXHAUSTED" || res?.status === 402;

export const DESCRIPTION_LANGUAGES = [
  "English",
  "Hindi",
  "Hinglish",
  "Bengali",
  "Gujarati",
  "Marathi",
  "Tamil",
  "Telugu",
  "Kannada",
  "Malayalam",
  "Odia",
  "Punjabi",
  "Urdu",
  "Assamese",
  "Kutchi",
] as const;

const LANG_KEY = "hkp.ai.descriptionLanguage";
const BROWSER_TO_LANGUAGE: Record<string, string> = {
  hi: "Hindi",
  bn: "Bengali",
  gu: "Gujarati",
  mr: "Marathi",
  ta: "Tamil",
  te: "Telugu",
  kn: "Kannada",
  ml: "Malayalam",
  or: "Odia",
  pa: "Punjabi",
  ur: "Urdu",
  as: "Assamese",
};

/** The language last picked on this device, else the browser's, else English. */
export const defaultDescriptionLanguage = (): string => {
  try {
    const saved = localStorage.getItem(LANG_KEY);
    if (saved && (DESCRIPTION_LANGUAGES as readonly string[]).includes(saved)) return saved;
  } catch {
    // storage blocked: fall through
  }
  const code = String((typeof navigator !== "undefined" && navigator.language) || "").slice(0, 2).toLowerCase();
  return BROWSER_TO_LANGUAGE[code] || "English";
};

export const rememberDescriptionLanguage = (language: string) => {
  try {
    localStorage.setItem(LANG_KEY, language);
  } catch {
    // a convenience only
  }
};
